import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import BackButton from '../../components/BackButton';
import Modal from '../../components/Modal';
import {
  Bot,
  Plus,
  SmilePlus,
  MoreVertical,
  Trash2,
  Loader2,
  CheckCircle,
  AlertTriangle,
  ChevronUp,
  ChevronDown,
  ImagePlus,
} from 'lucide-react';
import {
  AGENT_DEPARTMENTS,
  AGENT_SECTIONS,
  getAgentGroupEmojis,
  saveAgentGroupEmoji,
  listAgentTemplates,
  createAgentTemplate,
  deleteAgentTemplate,
  saveAgentsOrder,
} from '../../services/agentTemplatesService';
import PortalBackground from '../../components/PortalBackground';
import BBALoader from '../../components/BBALoader';
// Lazy-loaded so the full emoji picker (~large bundle) only downloads when opened.
const EmojiPicker = React.lazy(() => import('emoji-picker-react'));

const TABS = AGENT_DEPARTMENTS;

/**
 * CRM manager for agent templates. Mirrors Automation Templates: department
 * pill tabs with the shared emoji picker, compact cards (GIF tile + version
 * chip), reorder arrows, and a kebab menu for delete. Routines, abilities,
 * library, skills, and examples are edited on the agent's overview page.
 */
const AgentTemplates = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();

  const [activeTab, setActiveTab] = useState('lead');
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuKey, setMenuKey] = useState(null);

  // Department emoji picker
  const [groupEmojis, setGroupEmojis] = useState({});
  const [emojiTabId, setEmojiTabId] = useState(null);
  const [emojiSaving, setEmojiSaving] = useState(false);

  // Add-agent modal
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', description: '', category: 'lead' });
  const [addGif, setAddGif] = useState(null);
  const [addGifPreview, setAddGifPreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [addError, setAddError] = useState('');
  const gifInputRef = useRef(null);

  const active = TABS.find((t) => t.id === activeTab) || TABS[0];
  const emojiOf = (tabId) => groupEmojis[tabId] || TABS.find((t) => t.id === tabId)?.emoji || '🤖';
  const userName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'BBA Team';

  const card = 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800';
  const subtle = darkMode ? 'text-gray-400' : 'text-gray-500';
  const chipGood =
    'inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
  const chipMuted = `inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full ${
    darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'
  }`;
  const inputClass = `w-full rounded-xl border px-3 py-2 text-sm ${
    darkMode
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`;
  const labelClass = `block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`;

  useEffect(() => {
    setMenuKey(null);
    setError(null);
  }, [activeTab]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const rows = await listAgentTemplates(activeTab);
        if (!cancelled) setAgents(rows);
      } catch (e) {
        console.error('Error fetching agents:', e);
        if (!cancelled) {
          setAgents([]);
          setError(e.message || 'Failed to load agent templates.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  useEffect(() => {
    getAgentGroupEmojis().then(setGroupEmojis).catch(() => {});
  }, []);

  // Revoke the add-form preview URL when it changes or the page unmounts
  useEffect(() => () => addGifPreview && URL.revokeObjectURL(addGifPreview), [addGifPreview]);

  const refreshList = async () => setAgents(await listAgentTemplates(activeTab));

  // ---- Department emoji ----
  const pickGroupEmoji = async (emoji) => {
    if (!emojiTabId) return;
    setEmojiSaving(true);
    setError(null);
    try {
      await saveAgentGroupEmoji(emojiTabId, emoji);
      setGroupEmojis((prev) => ({ ...prev, [emojiTabId]: emoji }));
      setEmojiTabId(null);
    } catch (err) {
      console.error('Emoji save failed:', err);
      setError(err.message || 'Could not save the emoji.');
    } finally {
      setEmojiSaving(false);
    }
  };

  // ---- Add agent ----
  const openAdd = () => {
    setAddForm({ name: '', description: '', category: activeTab });
    setAddGif(null);
    setAddGifPreview('');
    setAddError('');
    setAddOpen(true);
  };

  const chooseAddGif = (file) => {
    if (!file) return;
    if (!(file.type === 'image/gif' || file.name.toLowerCase().endsWith('.gif'))) {
      setAddError('The agent image must be a .gif file.');
      return;
    }
    setAddError('');
    setAddGif(file);
    setAddGifPreview(URL.createObjectURL(file));
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!addForm.name.trim()) {
      setAddError('Enter an agent name.');
      return;
    }
    setSubmitting(true);
    setAddError('');
    try {
      const id = await createAgentTemplate({
        ...addForm,
        gifFile: addGif,
        user: { uid: currentUser?.uid, name: userName },
      });
      setAddOpen(false);
      // Straight to the overview to fill in routines, abilities, library, skills, examples
      navigate(`/crm/agent-templates/${id}?edit=1`);
    } catch (err) {
      console.error('Error creating agent:', err);
      setAddError(err.message || 'Could not create the agent.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Reorder (display order here and on the portal) ----
  const moveAgent = async (row, direction) => {
    const idx = agents.findIndex((r) => r.id === row.id);
    const target = idx + direction;
    if (idx === -1 || target < 0 || target >= agents.length) return;
    const next = [...agents];
    [next[idx], next[target]] = [next[target], next[idx]];
    setAgents(next.map((r, i) => ({ ...r, sortOrder: i })));
    try {
      await saveAgentsOrder(next.map((r) => r.id));
    } catch (err) {
      console.error('Reorder failed:', err);
      await refreshList();
    }
  };

  // ---- Delete ----
  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.name}"? Its GIF and version history are removed.`)) return;
    setError(null);
    try {
      await deleteAgentTemplate(row.id);
      await refreshList();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Delete failed.');
    }
  };

  const arrowClass = `p-1.5 rounded-md disabled:opacity-25 disabled:cursor-default ${
    darkMode
      ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700 disabled:hover:bg-transparent disabled:hover:text-gray-400'
      : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:hover:bg-transparent disabled:hover:text-gray-400'
  }`;

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative w-full max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <BackButton onClick={() => navigate('/crm/templates')} />
          <h1 className="text-3xl font-bold tracking-tight flex-1 text-center text-gray-900 dark:text-white">
            Agent Templates
          </h1>
          <span className="w-9" />
        </div>

        {/* Department tabs (the smile button on the active pill picks its emoji) */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <div
                key={tab.id}
                className={`inline-flex items-center rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-purple-600 text-white'
                    : darkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 ${isActive ? 'pr-1' : ''}`}
                >
                  <span className="mr-1.5">{emojiOf(tab.id)}</span>
                  {tab.label}
                </button>
                {isActive && (
                  <button
                    type="button"
                    onClick={() => setEmojiTabId(tab.id)}
                    className="pl-1 pr-3 py-2 text-white/80 hover:text-white"
                    title="Change department emoji"
                  >
                    <SmilePlus className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <p className={`text-sm mb-6 ${subtle}`}>{active.blurb}</p>

        {error && (
          <div
            className={`mb-4 rounded-md border px-4 py-3 text-sm ${
              darkMode ? 'border-red-800 bg-red-950/50 text-red-200' : 'border-red-200 bg-red-50 text-red-800'
            }`}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <BBALoader size={40} />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {agents.map((row, rowIndex) => {
              const menuOpen = menuKey === row.id;
              const counts = AGENT_SECTIONS.map((s) => ({ ...s, n: row[s.key]?.length || 0 })).filter((s) => s.n);
              return (
                <div
                  key={row.id}
                  className={`${card} rounded-2xl shadow-sm p-4 flex flex-col cursor-pointer hover:shadow-lg transition-shadow`}
                  onClick={() => navigate(`/crm/agent-templates/${row.id}`)}
                >
                  {/* Header: GIF tile + title/description + reorder/kebab */}
                  <div className="flex items-start gap-2.5 mb-3">
                    <div
                      className={`shrink-0 w-12 h-12 rounded-lg overflow-hidden flex items-center justify-center ${
                        darkMode ? 'bg-gray-700' : 'bg-gray-100'
                      }`}
                    >
                      {row.gif?.url ? (
                        <img src={row.gif.url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Bot className={`h-5 w-5 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">{row.name}</h3>
                      {row.description && (
                        <p className={`text-xs ${subtle} mt-0.5 line-clamp-2`}>{row.description}</p>
                      )}
                    </div>
                    <div className="relative shrink-0 flex items-center" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => moveAgent(row, -1)}
                        disabled={rowIndex === 0}
                        className={arrowClass}
                        title="Move up"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveAgent(row, 1)}
                        disabled={rowIndex === agents.length - 1}
                        className={arrowClass}
                        title="Move down"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setMenuKey(menuOpen ? null : row.id)}
                        className={`p-1.5 rounded-md ${
                          darkMode
                            ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                            : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                        title="More options"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {menuOpen && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setMenuKey(null)} />
                          <div
                            className={`absolute right-0 top-8 z-20 w-44 rounded-lg shadow-lg border py-1 ${
                              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                setMenuKey(null);
                                handleDelete(row);
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${
                                darkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-50'
                              }`}
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Delete agent
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Footer: version chip + section counts */}
                  <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-1">
                    <span className={chipGood}>
                      <CheckCircle className="w-3 h-3" /> v{row.version || '1.0'}
                    </span>
                    {counts.map((s) => (
                      <span key={s.key} className={chipMuted} title={s.label}>
                        {s.emoji} {s.n}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Add agent tile */}
            <button
              type="button"
              onClick={openAdd}
              className={`rounded-lg border-2 border-dashed p-4 flex flex-col items-center justify-center gap-2 min-h-[120px] transition-colors ${
                darkMode
                  ? 'border-gray-600 text-gray-400 hover:text-gray-200 hover:border-gray-500'
                  : 'border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'
              }`}
            >
              <Plus className="w-6 h-6" />
              <span className="text-sm font-medium">Add agent</span>
            </button>
          </div>
        )}

        {/* Add agent modal */}
        <Modal isOpen={addOpen} onClose={() => !submitting && setAddOpen(false)} maxWidth="md">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Add agent</h2>
          <p className={`text-sm ${subtle} mb-4`}>
            Adds a new agent to “{TABS.find((t) => t.id === addForm.category)?.label || active.label}”. You'll fill in
            its routines, abilities, library, skills, and examples next.
          </p>
          <form onSubmit={submitAdd} className="space-y-4">
            <div className="flex items-start gap-3">
              <div>
                <span className={labelClass}>GIF</span>
                <button
                  type="button"
                  onClick={() => gifInputRef.current?.click()}
                  title="Upload a GIF"
                  className={`w-[58px] h-[58px] rounded-md border overflow-hidden flex items-center justify-center transition-colors ${
                    darkMode ? 'border-gray-600 bg-gray-700 hover:bg-gray-600' : 'border-gray-300 bg-white hover:bg-gray-100'
                  }`}
                >
                  {addGifPreview ? (
                    <img src={addGifPreview} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <ImagePlus className={`w-5 h-5 ${subtle}`} />
                  )}
                </button>
                <input
                  ref={gifInputRef}
                  type="file"
                  accept="image/gif,.gif"
                  className="hidden"
                  onChange={(e) => {
                    chooseAddGif(e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
              </div>
              <div className="flex-1">
                <label htmlFor="agent-name" className={labelClass}>
                  Name
                </label>
                <input
                  id="agent-name"
                  type="text"
                  value={addForm.name}
                  onChange={(e) => setAddForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Lead follow-up agent"
                  className={inputClass}
                  disabled={submitting}
                  autoFocus
                />
              </div>
            </div>
            <div>
              <label htmlFor="agent-description" className={labelClass}>
                Description
              </label>
              <textarea
                id="agent-description"
                value={addForm.description}
                onChange={(e) => setAddForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="What this agent does"
                rows={2}
                className={inputClass}
                disabled={submitting}
              />
            </div>
            <div>
              <label htmlFor="agent-category" className={labelClass}>
                Department
              </label>
              <select
                id="agent-category"
                value={addForm.category}
                onChange={(e) => setAddForm((prev) => ({ ...prev, category: e.target.value }))}
                className={inputClass}
                disabled={submitting}
              >
                {TABS.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            {addError && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" /> {addError}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setAddOpen(false)}
                disabled={submitting}
                className={`px-4 py-2 rounded-md text-sm ${
                  darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
                }`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !addForm.name.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Creating…
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" /> Create
                  </>
                )}
              </button>
            </div>
          </form>
        </Modal>

        {/* Department emoji picker */}
        <Modal isOpen={!!emojiTabId} onClose={() => !emojiSaving && setEmojiTabId(null)} maxWidth="xl" zIndex={100}>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1 text-center">Choose an emoji</h2>
          <p className={`text-sm mb-4 text-center ${subtle}`}>
            {TABS.find((t) => t.id === emojiTabId)?.label || ''} department
          </p>
          <div className="flex justify-center">
            {emojiTabId && (
              <React.Suspense
                fallback={
                  <div className="flex justify-center items-center h-72 w-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
                  </div>
                }
              >
                <EmojiPicker
                  onEmojiClick={(emojiData) => !emojiSaving && pickGroupEmoji(emojiData.emoji)}
                  theme={darkMode ? 'dark' : 'light'}
                  width="100%"
                  height={380}
                  lazyLoadEmojis
                  previewConfig={{ showPreview: false }}
                />
              </React.Suspense>
            )}
          </div>
          <div className="flex items-center justify-between mt-4">
            {groupEmojis[emojiTabId] ? (
              <button
                onClick={() => pickGroupEmoji('')}
                disabled={emojiSaving}
                className="px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
              >
                Use default
              </button>
            ) : (
              <span />
            )}
            <button
              onClick={() => setEmojiTabId(null)}
              disabled={emojiSaving}
              className={`px-4 py-2 rounded-md text-sm ${
                darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {emojiSaving ? 'Saving…' : 'Cancel'}
            </button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default AgentTemplates;
